import { Button, CheckIcon, ExclamationmarkIcon, Header } from '@bp/ui-components';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { priorityToSeverity } from '../../../utils/timetable/severity';
import { testTimetableVersion } from '../../../utils/timetable/timetableVersion';
import { groupWarningsBySeverity } from '../../../utils/timetable/warningGroups';
import { TestWarningGroup } from '../TestWarningGroup/TestWarningGroup';
import { useTimetableStore } from '../TimetableProvider';
import styles from './wizard.module.scss';

export const WizardStep1Preflight: FC = observer(() => {
  const { t } = useTranslation();
  const pinboardStore = useTimetableStore();
  const solver = pinboardStore.solverStore;
  const versionUuid = pinboardStore.getCurrentVersion()?.uuid;

  const runPreflight = useCallback(() => {
    if (!versionUuid) {
      solver.setPreflightError(t('pinboard.generate.wizard.step1.error.noVersion'));
      return;
    }
    const classUuids =
      pinboardStore.context === 'classes' && pinboardStore.selectedRows.length > 0
        ? pinboardStore.selectedRows.map((row) => row.value)
        : pinboardStore.getClassRows().map((row) => row.value);
    // No classes means there is nothing to test — an empty backend result would otherwise be
    // shown as "all clean" and auto-advance the user straight to the solver (P0-10).
    if (classUuids.length === 0) {
      solver.setPreflightError(t('pinboard.generate.wizard.step1.error.noClasses'));
      return;
    }
    // Missing master data the backend test would otherwise report as "clean" (P2-8a): nothing to
    // schedule, or no schedulable periods at all. Both make solving impossible — block hard.
    if (pinboardStore.minimalCards.length === 0) {
      solver.setPreflightError(t('pinboard.generate.wizard.step1.error.noCards'));
      return;
    }
    if ((pinboardStore.timeGridEntries ?? []).filter((entry) => !entry.pause).length === 0) {
      solver.setPreflightError(t('pinboard.generate.wizard.step1.error.noTimeGrid'));
      return;
    }
    solver.setPreflightError(null);
    solver.setPreflightLoading(true);
    testTimetableVersion(versionUuid, classUuids)
      .then((result) => {
        // apiHelper returns undefined on a non-OK HTTP response — treat that as a failure,
        // not as "no warnings", so the user isn't tricked into thinking the plan is clean.
        if (result === undefined) {
          solver.setPreflightError(t('pinboard.generate.wizard.step1.error.body'));
          return;
        }
        solver.setPreflightResult(result);
      })
      .catch((err) => {
        console.error('preflight test failed', err);
        solver.setPreflightError(t('pinboard.generate.wizard.step1.error.body'));
      })
      .finally(() => {
        solver.setPreflightLoading(false);
      });
  }, [versionUuid, pinboardStore, solver, t]);

  // Trigger the preflight only when there's no cached result, error, or in-flight call.
  useEffect(() => {
    if (solver.preflightResult !== null || solver.preflightLoading || solver.preflightError !== null) {
      return;
    }
    runPreflight();
  }, [runPreflight, solver.preflightResult, solver.preflightLoading, solver.preflightError]);

  // Auto-advance to step 2 when a fresh, clean preflight arrives during this mount.
  // Snapshotting the result on mount means "← Zurück" from step 2 won't immediately push
  // the user forward again — they can still review step 1 when there are no warnings.
  const hadResultOnMount = useRef(solver.preflightResult !== null);
  useEffect(() => {
    if (hadResultOnMount.current) return;
    if (solver.preflightLoading || solver.preflightError !== null) return;
    if (solver.preflightResult !== null && solver.preflightResult.length === 0 && solver.wizardStep === 1) {
      solver.nextWizardStep();
    }
  }, [solver, solver.preflightLoading, solver.preflightError, solver.preflightResult, solver.wizardStep]);

  const warnings = solver.preflightResult ?? [];
  const hasWarnings = warnings.length > 0;
  const hasError = solver.preflightError !== null;

  const blockerCount = warnings.filter((w) => priorityToSeverity(w.priority) === 'error').length;

  return (
    <div style={{ display: 'block' }}>
      <Header headline={t('pinboard.generate.wizard.step1.title')} size='s' noMargin />
      <p className={styles['section-sub']}>{t('pinboard.generate.wizard.step1.sub')}</p>

      {hasError ? (
        <div className={classNames(styles['check-item'], styles.warn)}>
          <div className={classNames(styles['check-icon'], styles.warn)}>
            <ExclamationmarkIcon className='svg-icon' />
          </div>
          <div className={styles['check-body']}>
            <div className={styles['check-title']}>{t('pinboard.generate.wizard.step1.error.title')}</div>
            <div className={styles['check-desc']}>{solver.preflightError}</div>
            <div className={styles['preflight-actions']}>
              <Button hierarchy='secondary' onClick={runPreflight}>
                {t('pinboard.generate.wizard.step1.error.retry')}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles['check-list']}>
          {!hasWarnings ? (
            <div className={classNames(styles['check-item'], styles.ok)}>
              <div className={classNames(styles['check-icon'], styles.ok)}>
                <CheckIcon className='svg-icon' />
              </div>
              <div className={styles['check-body']}>
                <div className={styles['check-title']}>{t('pinboard.generate.wizard.step1.allOk.title')}</div>
                <div className={styles['check-desc']}>{t('pinboard.generate.wizard.step1.allOk.desc')}</div>
              </div>
            </div>
          ) : (
            groupWarningsBySeverity(warnings).map(([type, groupWarnings]) => (
              <TestWarningGroup key={type} type={type} warnings={groupWarnings} showInPlanEnabled={false} />
            ))
          )}
        </div>
      )}

      {!hasError && hasWarnings ? (
        blockerCount > 0 ? (
          <div className={classNames(styles['preflight-summary'], styles.err)}>
            <span className={styles['summary-title']}>
              {t('pinboard.generate.wizard.step1.summary.errTitle', { count: blockerCount })}
            </span>{' '}
            {t('pinboard.generate.wizard.step1.summary.errBody')}
          </div>
        ) : (
          <div className={classNames(styles['preflight-summary'], styles.warn)}>
            {t('pinboard.generate.wizard.step1.summary.okBody')}
          </div>
        )
      ) : !hasError ? (
        <div className={classNames(styles['preflight-summary'], styles.ok)}>
          {t('pinboard.generate.wizard.step1.summary.okBody')}
        </div>
      ) : null}
    </div>
  );
});
